import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Input,
  Button,
  message as antdMessage,
  Avatar,
  Badge,
  Empty,
  Spin,
  Select,
  Dropdown,
  Skeleton,
} from "antd";
import {
  PlusOutlined,
  CloseCircleFilled,
  MoreOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getWhatsappChats,
  getWhatsappMessagesByPhone,
  sendWhatsappMessageFromCRM,
  sendWhatsappImageFromCRM,
} from "../features/customers/customerSlice";
import { FaWhatsapp, FaSearch } from "react-icons/fa";
import { base_url } from "../utils/baseUrl";
import { useMediaQuery } from "react-responsive";

const { Title, Text } = Typography;
const { TextArea } = Input;

const FOLLOW_UP_TEMPLATE_MESSAGE =
  "Hello, we are following up on your earlier inquiry. Please reply here if you would like us to continue assisting you.";

const MAX_IMAGES = 20;

const WhatsappPage = () => {
  const dispatch = useDispatch();

  const { whatsappChats, whatsappMessages, isLoading } = useSelector(
    (state) => state.customer
  );

  const now = new Date();
  const isDesktop = useMediaQuery({ minWidth: 785 });

  const [selectedPhone, setSelectedPhone] = useState(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedWeek, setSelectedWeek] = useState("all");
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSendingImage, setIsSendingImage] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(getWhatsappChats());
  }, [dispatch]);

  const chatsLoading = isLoading && (!whatsappChats || whatsappChats.length === 0);

  const lastIncomingCustomerMessage = useMemo(() => {
    return [...(whatsappMessages || [])]
      .filter(
        (msg) => msg.direction === "incoming" && msg.category === "customer"
      )
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  }, [whatsappMessages]);

  const isSessionActive = useMemo(() => {
    if (!selectedPhone) return false;
    if (!lastIncomingCustomerMessage?.createdAt) return false;

    const lastIncomingTime = new Date(
      lastIncomingCustomerMessage.createdAt
    ).getTime();

    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

    return Date.now() - lastIncomingTime <= TWENTY_FOUR_HOURS;
  }, [selectedPhone, lastIncomingCustomerMessage]);

  useEffect(() => {
    if (!selectedPhone) {
      setNewMessage("");
      clearAllImages();
      return;
    }

    if (isSessionActive) {
      setNewMessage("");
    } else {
      setNewMessage(FOLLOW_UP_TEMPLATE_MESSAGE);
    }

    clearAllImages();
  }, [selectedPhone, isSessionActive]);

  const handleSelectChat = async (phone, customerName = "") => {
    try {
      setSelectedPhone(phone);
      setSelectedCustomerName(customerName || "");
      await dispatch(getWhatsappMessagesByPhone(phone)).unwrap();
      await dispatch(getWhatsappChats()).unwrap();
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      antdMessage.error("Failed to fetch messages");
    }
  };

  const handlePickImage = () => {
    if (!selectedPhone) {
      antdMessage.warning("Select a chat first");
      return;
    }

    if (!isSessionActive) {
      antdMessage.warning(
        "Session expired. Image messages are disabled until the customer replies."
      );
      return;
    }

    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (allowedTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length) {
      antdMessage.error(
        `Unsupported files skipped: ${invalidFiles.join(", ")}`
      );
    }

    const remainingSlots = MAX_IMAGES - selectedImages.length;

    if (remainingSlots <= 0) {
      antdMessage.warning(`You can only upload up to ${MAX_IMAGES} images`);
      e.target.value = "";
      return;
    }

    const filesToAdd = validFiles
      .slice(0, remainingSlots)
      .map((file, index) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${index}`,
        file,
        preview: URL.createObjectURL(file),
      }));

    if (validFiles.length > remainingSlots) {
      antdMessage.warning(
        `Only ${remainingSlots} more image(s) can be added`
      );
    }

    setSelectedImages((prev) => [...prev, ...filesToAdd]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const clearAllImages = () => {
    setSelectedImages((prev) => {
      prev.forEach((item) => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
      return [];
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeSelectedImage = (id) => {
    setSelectedImages((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.preview) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter((item) => item.id !== id);
    });
  };

  const handleSendMessage = async () => {
    if (!selectedPhone) {
      antdMessage.warning("Select a chat first");
      return;
    }

    if (!newMessage.trim()) {
      antdMessage.warning(
        isSessionActive ? "Type a message first" : "No template message found"
      );
      return;
    }

    try {
      const result = await dispatch(
        sendWhatsappMessageFromCRM({
          phone: selectedPhone,
          message: newMessage.trim(),
        })
      ).unwrap();

      if (result?.success === false) {
        throw new Error(
          result?.error || result?.message || "Failed to send message"
        );
      }

      if (isSessionActive) {
        setNewMessage("");
      } else {
        setNewMessage(FOLLOW_UP_TEMPLATE_MESSAGE);
      }

      antdMessage.success(
        result?.sentWith === "template"
          ? "Session expired. Follow-up template sent"
          : "Message sent"
      );

      await dispatch(getWhatsappMessagesByPhone(selectedPhone)).unwrap();
      await dispatch(getWhatsappChats()).unwrap();
    } catch (error) {
      console.error("Send message error:", error);
      antdMessage.error(
        error?.error ||
          error?.message ||
          error?.details?.error ||
          "Failed to send message"
      );
    }
  };

  const handleSendImages = async () => {
    if (!selectedPhone) {
      antdMessage.warning("Select a chat first");
      return;
    }

    if (!isSessionActive) {
      antdMessage.warning(
        "Session expired. You can only send images after the customer replies."
      );
      return;
    }

    if (!selectedImages.length) {
      antdMessage.warning("Choose image(s) first");
      return;
    }

    try {
      setIsSendingImage(true);

      const formData = new FormData();
      formData.append("phone", selectedPhone);
      formData.append("caption", newMessage.trim());

      selectedImages.forEach((item) => {
        formData.append("images", item.file);
      });

      const result = await dispatch(sendWhatsappImageFromCRM(formData)).unwrap();

      if (result?.success === false || !result?.data?.length) {
        throw new Error(
          result?.error ||
            result?.message ||
            "No images were sent successfully"
        );
      }

      const actualSentCount = result?.data?.length || 0;

      clearAllImages();
      setNewMessage("");
      antdMessage.success(`${actualSentCount} image(s) sent successfully`);

      await dispatch(getWhatsappMessagesByPhone(selectedPhone)).unwrap();
      await dispatch(getWhatsappChats()).unwrap();
    } catch (error) {
      console.error("Send image error:", error);
      antdMessage.error(
        error?.error ||
          error?.message ||
          error?.details?.error ||
          "Failed to send images"
      );
    } finally {
      setIsSendingImage(false);
    }
  };

  const formatTime = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleString();
  };

  const getInitials = (name, phone) => {
    if (name && name.trim()) {
      const parts = name.trim().split(" ");
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (
        parts[0].charAt(0).toUpperCase() +
        parts[1].charAt(0).toUpperCase()
      );
    }
    return phone?.slice(-2) || "?";
  };

  const getWeekOfMonth = (dateValue) => {
    const date = new Date(dateValue);
    return Math.ceil(date.getDate() / 7);
  };

  const normalizeHref = (value = "") => {
    const trimmed = String(value).trim();
    if (!trimmed) return "#";
    if (/^(https?:\/\/|tel:)/i.test(trimmed)) return trimmed;
    if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
    return trimmed;
  };

  const normalizePhoneForTel = (value = "") => {
    const digits = String(value).replace(/\D/g, "");

    if (/^0[17]\d{8}$/.test(digits)) {
      return `+254${digits.slice(1)}`;
    }

    if (/^254[17]\d{8}$/.test(digits)) {
      return `+${digits}`;
    }

    return "";
  };

  const isKenyanPhoneLike = (value = "") => {
    const normalized = String(value).trim();

    return (
      /^0[17]\d{8}$/.test(normalized.replace(/[^\d]/g, "")) ||
      /^254[17]\d{8}$/.test(normalized.replace(/[^\d]/g, "")) ||
      /^\+254[17]\d{8}$/.test(normalized.replace(/[\s().-]/g, ""))
    );
  };

  const renderTextWithLinks = (text = "") => {
    if (!text) return "No text";

    const regex =
      /(\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+|\+254[\d\s().-]{9,}|254[\d\s().-]{9,}|0[\d\s().-]{9,})/gi;

    const parts = String(text).split(regex);

    return parts.map((part, index) => {
      if (!part) return null;

      const trimmed = part.trim();

      if (/^(https?:\/\/|www\.)/i.test(trimmed)) {
        return (
          <a
            key={`${trimmed}-${index}`}
            href={normalizeHref(trimmed)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1677ff",
              textDecoration: "underline",
              wordBreak: "break-word",
            }}
          >
            {part}
          </a>
        );
      }

      if (isKenyanPhoneLike(trimmed)) {
        const telValue = normalizePhoneForTel(trimmed);

        if (telValue) {
          return (
            <a
              key={`${trimmed}-${index}`}
              href={`tel:${telValue}`}
              style={{
                color: "#1677ff",
                textDecoration: "underline",
                wordBreak: "break-word",
              }}
            >
              {part}
            </a>
          );
        }
      }

      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    });
  };

  const filteredChats = useMemo(() => {
    let chats = whatsappChats || [];

    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      chats = chats.filter(
        (chat) =>
          chat.phone?.toLowerCase().includes(query) ||
          chat.name?.toLowerCase().includes(query) ||
          chat.lastMessage?.toLowerCase().includes(query)
      );
    }

    chats = chats.filter((chat) => {
      if (!chat.lastMessageAt) return false;

      const chatDate = new Date(chat.lastMessageAt);

      const monthMatch =
        selectedMonth === "all" || chatDate.getMonth() === selectedMonth;

      const weekMatch =
        selectedWeek === "all" ||
        getWeekOfMonth(chat.lastMessageAt) === selectedWeek;

      return monthMatch && weekMatch;
    });

    return chats;
  }, [whatsappChats, searchText, selectedMonth, selectedWeek]);

  const totalChats = filteredChats.length;
  const totalUnreadMessages = filteredChats.reduce(
    (sum, chat) => sum + Number(chat.unreadCount || 0),
    0
  );
  const activeChats = filteredChats.filter(
    (chat) => new Date(chat.lastMessageAt).toDateString() === now.toDateString()
  ).length;

  const selectedChatUnread = filteredChats.find(
    (chat) => chat.phone === selectedPhone
  )?.unreadCount;

  const months = [
    { value: "all", label: "All Months" },
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  const weeks = [
    { value: "all", label: "All Weeks" },
    { value: 1, label: "Week 1" },
    { value: 2, label: "Week 2" },
    { value: 3, label: "Week 3" },
    { value: 4, label: "Week 4" },
    { value: 5, label: "Week 5" },
  ];

  const getMediaUrl = (item) =>
    `${base_url}crm/whatsapp/media/${item.mediaId}`;

  const getDownloadUrl = (item) =>
    `${base_url}crm/whatsapp/media/${item.mediaId}?download=1&filename=${encodeURIComponent(
      item.mediaFilename || "file"
    )}`;

  const renderMediaMenu = (item) => {
    if (!item?.mediaId) return null;

    const items = [
      {
        key: "open",
        label: (
          <a
            href={getMediaUrl(item)}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Open
          </a>
        ),
      },
      {
        key: "download",
        label: (
          <a
            href={getDownloadUrl(item)}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            Download
          </a>
        ),
      },
    ];

    return (
      <Dropdown
        menu={{ items }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 30,
            height: 30,
            minWidth: 30,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#111b21",
          }}
        />
      </Dropdown>
    );
  };

  const renderMessageContent = (item) => {
    if (item.type === "image") {
      return (
        <div>
          {item.mediaId ? (
            <div style={{ position: "relative", display: "inline-block" }}>
              <img
                src={getMediaUrl(item)}
                alt={item.mediaFilename || "WhatsApp image"}
                style={{
                  width: "100%",
                  maxWidth: 260,
                  borderRadius: 10,
                  display: "block",
                  marginBottom: item.text ? 8 : 0,
                }}
              />
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                maxWidth: 260,
                minHeight: 120,
                borderRadius: 10,
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#667781",
                fontSize: 13,
                marginBottom: item.text ? 8 : 0,
              }}
            >
              Image unavailable
            </div>
          )}

          {item.text ? (
            <div
              style={{
                fontSize: 14,
                color: "#111b21",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderTextWithLinks(item.text)}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "document") {
      return (
        <div>
          {item.mediaId ? (
            <div
              style={{
                position: "relative",
                padding: "10px 44px 10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#111b21",
                fontWeight: 500,
                marginBottom: item.text ? 8 : 0,
              }}
            >
              📄 {item.mediaFilename || "Document"}
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
                marginBottom: item.text ? 8 : 0,
              }}
            >
              📄 Document unavailable
            </div>
          )}

          {item.text ? (
            <div
              style={{
                fontSize: 14,
                color: "#111b21",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderTextWithLinks(item.text)}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "video") {
      return (
        <div>
          {item.mediaId ? (
            <div
              style={{
                position: "relative",
                padding: "10px 44px 10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#111b21",
                fontWeight: 500,
                marginBottom: item.text ? 8 : 0,
              }}
            >
              🎥 {item.mediaFilename || "Video"}
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
                marginBottom: item.text ? 8 : 0,
              }}
            >
              🎥 Video unavailable
            </div>
          )}

          {item.text ? (
            <div
              style={{
                fontSize: 14,
                color: "#111b21",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {renderTextWithLinks(item.text)}
            </div>
          ) : null}
        </div>
      );
    }

    if (item.type === "audio") {
      return (
        <div>
          {item.mediaId ? (
            <div
              style={{
                position: "relative",
                padding: "10px 44px 10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#111b21",
                fontWeight: 500,
              }}
            >
              🎵 Audio
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
              }}
            >
              🎵 Audio unavailable
            </div>
          )}
        </div>
      );
    }

    if (item.type === "sticker") {
      return (
        <div>
          {item.mediaId ? (
            <div style={{ position: "relative", display: "inline-block" }}>
              <img
                src={getMediaUrl(item)}
                alt="Sticker"
                style={{
                  width: 120,
                  maxWidth: "100%",
                  borderRadius: 10,
                  display: "block",
                }}
              />
              {renderMediaMenu(item)}
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "#f5f5f5",
                color: "#667781",
              }}
            >
              Sticker unavailable
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          fontSize: 14,
          color: "#111b21",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {renderTextWithLinks(item.text || "")}
      </div>
    );
  };

  const renderTopCardsSkeleton = () => (
    <div className="row mb-4 g-3">
      {[1, 2, 3].map((item) => (
        <div className="col-md-4" key={item}>
          <div className="bg-white p-4 rounded shadow-sm">
            <Skeleton.Input
              active
              size="small"
              style={{ width: 110, marginBottom: 12 }}
            />
            <Skeleton.Input active style={{ width: 80 }} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderChatListSkeleton = () => (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        background: "#fff",
      }}
    >
      {Array.from({ length: 7 }).map((_, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            gap: 12,
            padding: "14px 16px",
            borderBottom: "1px solid #f1f1f1",
            alignItems: "flex-start",
          }}
        >
          <Skeleton.Avatar active size={46} shape="circle" />
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <Skeleton.Input active size="small" style={{ width: "45%" }} />
              <Skeleton.Input active size="small" style={{ width: 50 }} />
            </div>
            <Skeleton.Input
              active
              size="small"
              style={{ width: "80%", marginBottom: 8 }}
            />
            <Skeleton.Input active size="small" style={{ width: "35%" }} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Carman Autos CRM WhatsApp
          </Title>
          <Text type="secondary">
            Manage customer conversations, unread messages, and replies in one place.
          </Text>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Select
            value={selectedMonth}
            style={{ width: 150 }}
            options={months}
            onChange={(value) => setSelectedMonth(value)}
          />
          <Select
            value={selectedWeek}
            style={{ width: 140 }}
            options={weeks}
            onChange={(value) => setSelectedWeek(value)}
          />
        </div>
      </div>

      {chatsLoading ? (
        renderTopCardsSkeleton()
      ) : (
        <div className="row mb-4 g-3">
          <div className="col-md-4">
            <div
              className="bg-white p-4 rounded shadow-sm"
              style={{ borderLeft: "4px solid #128c7e" }}
            >
              <small className="text-muted">Total Chats</small>
              <h4 style={{ marginBottom: 0 }}>{totalChats}</h4>
            </div>
          </div>

          <div className="col-md-4">
            <div
              className="bg-white p-4 rounded shadow-sm"
              style={{ borderLeft: "4px solid #f47e20" }}
            >
              <small className="text-muted">Unread Messages</small>
              <h4 style={{ marginBottom: 0 }}>{totalUnreadMessages}</h4>
            </div>
          </div>

          <div className="col-md-4">
            <div
              className="bg-white p-4 rounded shadow-sm"
              style={{ borderLeft: "4px solid #1677ff" }}
            >
              <small className="text-muted">Active Today</small>
              <h4 style={{ marginBottom: 0 }}>{activeChats}</h4>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "380px 1fr",
          height: "calc(130vh - 260px)",
          border: "1px solid #d9d9d9",
          borderRadius: 14,
          overflow: "hidden",
          background: "#fff",
          minHeight: 0,
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            borderRight: "1px solid #e8e8e8",
            display: "flex",
            flexDirection: "column",
            background: "#f7f8fa",
            minHeight: 0,
            height: "100%",
          }}
        >
          <div
            style={{
              padding: "16px 18px",
              background: "#075e54",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              fontWeight: 600,
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FaWhatsapp size={20} />
              <span>Inbox</span>
            </div>

            <Badge
              count={chatsLoading ? 0 : totalUnreadMessages}
              style={{ backgroundColor: "#f47e20" }}
            />
          </div>

          <div
            style={{
              padding: 12,
              background: "#f7f8fa",
              borderBottom: "1px solid #ececec",
              flexShrink: 0,
            }}
          >
            <Input
              placeholder="Search chats by name, phone, or message"
              prefix={<FaSearch />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: 10, height: 40 }}
            />
          </div>

          {chatsLoading ? (
            renderChatListSkeleton()
          ) : (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                background: "#fff",
              }}
            >
              {filteredChats.length > 0 ? (
                filteredChats.map((chat, index) => {
                  const isActive = selectedPhone === chat.phone;
                  const hasUnread = (chat.unreadCount || 0) > 0;

                  return (
                    <div
                      key={chat.phone || index}
                      onClick={() => handleSelectChat(chat.phone, chat.name)}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: "14px 16px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f1f1f1",
                        background: isActive
                          ? "#e6f7ff"
                          : hasUnread
                          ? "#f6ffed"
                          : "#fff",
                        alignItems: "flex-start",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Avatar
                        size={46}
                        style={{
                          backgroundColor:
                            hasUnread && !isActive ? "#25d366" : "#128c7e",
                          flexShrink: 0,
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(chat.name, chat.phone)}
                      </Avatar>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 4,
                            gap: 8,
                          }}
                        >
                          <Text
                            strong
                            ellipsis
                            style={{
                              maxWidth: 170,
                              color: hasUnread && !isActive ? "#075e54" : "#111b21",
                              fontWeight: hasUnread && !isActive ? 700 : 600,
                            }}
                          >
                            {chat.name || chat.phone}
                          </Text>

                          <Text
                            style={{
                              fontSize: 11,
                              whiteSpace: "nowrap",
                              color: hasUnread && !isActive ? "#25d366" : "#667781",
                              fontWeight: hasUnread && !isActive ? 700 : 400,
                            }}
                          >
                            {formatTime(chat.lastMessageAt)}
                          </Text>
                        </div>

                        <div
                          style={{
                            fontSize: 13,
                            color: hasUnread && !isActive ? "#111b21" : "#667781",
                            fontWeight: hasUnread && !isActive ? 600 : 400,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {chat.lastMessage || "No messages"}
                        </div>

                        <div
                          style={{
                            marginTop: 5,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {chat.phone}
                          </Text>

                          {chat.unreadCount > 0 ? (
                            <Badge
                              count={chat.unreadCount}
                              style={{ backgroundColor: "#25d366" }}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: 24 }}>
                  <Empty description="No chats found" />
                </div>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            background: "#efeae2",
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid #d9d9d9",
              background: "#f8f9fb",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexShrink: 0,
            }}
          >
            {selectedPhone ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Avatar style={{ backgroundColor: "#128c7e" }} size={42}>
                    {getInitials(selectedCustomerName, selectedPhone)}
                  </Avatar>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                      {selectedCustomerName || "Unknown"}
                    </div>
                    <div style={{ fontSize: 12, color: "#667781" }}>
                      {selectedPhone}
                    </div>
                  </div>
                </div>

                <div>
                  <Badge
                    count={selectedChatUnread || 0}
                    style={{ backgroundColor: "#f47e20" }}
                  />
                </div>
              </>
            ) : (
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Conversation</div>
                <div style={{ fontSize: 12, color: "#667781" }}>
                  Select a chat to view messages
                </div>
              </div>
            )}
          </div>

          {selectedPhone && !isSessionActive && (
            <div
              style={{
                padding: "10px 14px",
                background: "#fff7e6",
                borderTop: "1px solid #d9d9d9",
                borderBottom: "1px solid #d9d9d9",
                color: "#d46b08",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Session expired: a follow-up template will be sent instead of a
              normal message.
            </div>
          )}

          <div
            style={{
              flex: 1,
              minHeight: 0,
              height: 0,
              overflowY: "auto",
              overflowX: "hidden",
              padding: 18,
            }}
          >
            {isLoading && selectedPhone ? (
              <div style={{ textAlign: "center", paddingTop: 50 }}>
                <Spin />
              </div>
            ) : selectedPhone ? (
              (whatsappMessages || []).length > 0 ? (
                [...(whatsappMessages || [])]
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                  .map((item, index) => (
                    <div
                      key={item._id || index}
                      style={{
                        display: "flex",
                        justifyContent:
                          item.direction === "outgoing" ? "flex-end" : "flex-start",
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          background:
                            item.direction === "outgoing" ? "#d9fdd3" : "#fff",
                          padding: "10px 12px 8px",
                          borderRadius: 12,
                          maxWidth: "72%",
                          boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                          wordBreak: "break-word",
                        }}
                      >
                        {renderMessageContent(item)}

                        <div
                          style={{
                            marginTop: 5,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 6,
                            fontSize: 11,
                            color: "#667781",
                            flexWrap: "wrap",
                          }}
                        >
                          <span>{formatDateTime(item.createdAt)}</span>
                          <span>{item.status || "sent"}</span>
                        </div>
                      </div>
                    </div>
                  ))
              ) : (
                <Empty description="No messages found" />
              )
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Empty description="Select a chat to view messages" />
              </div>
            )}
          </div>

          {selectedImages.length > 0 && isSessionActive && (
            <div
              style={{
                padding: isDesktop ? "12px 14px" : "10px 12px",
                borderTop: "1px solid #d9d9d9",
                background: "#f8f9fb",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                flexShrink: 0,
                maxHeight: isDesktop ? 320 : 360,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  flexShrink: 0,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, color: "#111b21" }}>
                  {selectedImages.length} image(s) selected
                </div>

                <Button
                  danger
                  type="text"
                  onClick={clearAllImages}
                  style={{ paddingInline: 0 }}
                >
                  Clear all
                </Button>
              </div>

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: 4,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isDesktop
                      ? "repeat(auto-fill, minmax(170px, 1fr))"
                      : "repeat(2, minmax(0, 1fr))",
                    gap: 12,
                  }}
                >
                  {selectedImages.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        background: "#fff",
                        border: "1px solid #d9d9d9",
                        borderRadius: 14,
                        overflow: "hidden",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          aspectRatio: "1 / 1",
                          background: "#f5f5f5",
                        }}
                      >
                        <img
                          src={item.preview}
                          alt={item.file.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />

                        <Button
                          type="text"
                          icon={
                            <CloseCircleFilled
                              style={{ color: "#ff4d4f", fontSize: 18 }}
                            />
                          }
                          onClick={() => removeSelectedImage(item.id)}
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            background: "rgba(255,255,255,0.9)",
                            borderRadius: "50%",
                            width: 30,
                            height: 30,
                            minWidth: 30,
                          }}
                        />
                      </div>

                      <div
                        style={{
                          padding: 10,
                          fontSize: 12,
                          color: "#667781",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={item.file.name}
                      >
                        {item.file.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-end",
                  flexWrap: isDesktop ? "nowrap" : "wrap",
                  flexShrink: 0,
                  borderTop: "1px solid #e8e8e8",
                  paddingTop: 10,
                  background: "#f8f9fb",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextArea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Add one caption for all selected images"
                    autoSize={{ minRows: 2, maxRows: 4 }}
                  />
                </div>

                <Button
                  type="primary"
                  onClick={handleSendImages}
                  loading={isSendingImage}
                  icon={<FaWhatsapp />}
                  style={{
                    background: "#25d366",
                    borderColor: "#25d366",
                    color: "#fff",
                    borderRadius: 10,
                    fontWeight: 600,
                    height: 42,
                    minWidth: isDesktop ? 150 : "100%",
                    flexShrink: 0,
                  }}
                >
                  Send {selectedImages.length} Image
                  {selectedImages.length > 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          )}

          <div
            style={{
              padding: 14,
              borderTop: "1px solid #d9d9d9",
              background: "#f8f9fb",
              display: "flex",
              gap: 10,
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              multiple
              style={{ display: "none" }}
              onChange={handleImageChange}
            />

            {selectedPhone && isSessionActive && (
              <Button
                type="text"
                onClick={handlePickImage}
                style={{
                  width: 42,
                  height: 42,
                  minWidth: 42,
                  borderRadius: "50%",
                  border: "1px solid #d9d9d9",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#128c7e",
                  flexShrink: 0,
                }}
                icon={<PlusOutlined style={{ fontSize: 18 }} />}
              />
            )}

            <div style={{ flex: 1 }}>
              <TextArea
                rows={2}
                placeholder={
                  !selectedPhone
                    ? "Select a chat first"
                    : !isSessionActive
                    ? "Session expired. A follow-up template will be sent"
                    : selectedImages.length > 0
                    ? "Use the caption box above for all selected images"
                    : "Type a message to the customer"
                }
                value={selectedImages.length > 0 ? "" : newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={
                  !selectedPhone || !isSessionActive || selectedImages.length > 0
                }
                autoSize={{ minRows: 2, maxRows: 4 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    if (selectedImages.length > 0 && isSessionActive) {
                      handleSendImages();
                    } else {
                      handleSendMessage();
                    }
                  }
                }}
              />
            </div>

            <Button
              type="primary"
              onClick={
                selectedImages.length > 0 && isSessionActive
                  ? handleSendImages
                  : handleSendMessage
              }
              loading={selectedImages.length > 0 ? isSendingImage : isLoading}
              disabled={
                selectedImages.length > 0
                  ? !selectedPhone || !isSessionActive
                  : !selectedPhone || !newMessage.trim()
              }
              style={{
                background: "#128c7e",
                borderColor: "#128c7e",
                color: "#fff",
                height: 42,
                minWidth: 90,
                borderRadius: 8,
                flexShrink: 0,
              }}
              icon={
                selectedImages.length > 0 && isSessionActive ? <FaWhatsapp /> : null
              }
            >
              {selectedPhone
                ? selectedImages.length > 0 && isSessionActive
                  ? `Send ${selectedImages.length}`
                  : isSessionActive
                  ? "Send"
                  : "Send Template"
                : "Send"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsappPage;